import React from "react";
import { ArrowLeft, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";

const LAST_UPDATED = "April 18, 2026";

const Section = ({ title, children }) => (
  <div className="mb-6">
    <h2 className="text-base font-bold text-gray-900 mb-2">{title}</h2>
    <div className="text-sm text-gray-600 leading-relaxed space-y-2">{children}</div>
  </div>
);

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <div className="sticky top-0 z-10 bg-white border-b px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <Shield className="h-5 w-5 text-green-600" />
        <h1 className="font-bold text-gray-900">Privacy Policy</h1>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6" data-testid="privacy-policy-page">
        <p className="text-xs text-gray-400 mb-6">Last updated: {LAST_UPDATED}</p>

        <Section title="1. Introduction">
          <p>
            Foodz Lizts ("we," "our," or "us") operates the Foodz Lizts mobile application and website
            (the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard
            your information when you use our Service.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <p><strong>Account Information:</strong> Name, email address, phone number (optional), and encrypted PIN/password when you create an account.</p>
          <p><strong>Profile Information:</strong> Dietary preferences, food allergies, household size, and preferred stores that you provide during onboarding.</p>
          <p><strong>Location Data:</strong></p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Zip Code:</strong> Entered by you to display local weekly grocery ads and store availability.</li>
            <li><strong>GPS / Precise Location:</strong> With your explicit consent, we collect your device's GPS coordinates to find nearby stores, show deals within your selected radius, provide directions to deal locations, and auto-detect stores when posting community deals. <strong>Your precise location is processed in real-time only and is never stored on our servers or shared with other users.</strong> You can enable or disable location services at any time.</li>
          </ul>
          <p><strong>Grocery & Shopping Data:</strong> Grocery lists, meal plans, pantry items, budget information, and shopping patterns.</p>
          <p><strong>Receipt Data:</strong> When you scan receipts, we use optical character recognition (OCR) and AI to extract item names, prices, and store information. Receipt images are processed but not permanently stored. Extracted data is used to track your budget, improve brand recognition, and personalize your experience.</p>
          <p><strong>Community Content:</strong> Deals you post, comments, likes, saves, and interactions with other users' content in the Community Deals section.</p>
          <p><strong>Payment Data:</strong> Subscription payments are processed by PayPal. We do not store your credit card numbers or PayPal credentials. PayPal's privacy policy applies to payment data.</p>
          <p><strong>Device Data:</strong> Browser type, device type, and push notification tokens (if you opt in to notifications).</p>
        </Section>

        <Section title="3. How We Use Your Information">
          <p>We use the information we collect to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Provide, maintain, and improve our Service</li>
            <li>Personalize meal plans and recipes based on your dietary preferences and allergies</li>
            <li>Display relevant weekly grocery deals for your location</li>
            <li>Show nearby stores and provide directions using GPS data (with your consent)</li>
            <li>Filter community deals by distance from your location</li>
            <li>Recognize brands and auto-categorize items in your grocery lists using AI</li>
            <li>Learn your shopping patterns from receipts to improve product suggestions</li>
            <li>Track your grocery budget and spending</li>
            <li>Process subscription payments via PayPal</li>
            <li>Send account-related communications (trial notices, deal alerts, password resets)</li>
            <li>Operate the community deals platform (display posts, manage interactions)</li>
            <li>Administer the gamification and referral rewards programs</li>
            <li>Respond to your support requests</li>
          </ul>
        </Section>

        <Section title="4. AI & Automated Processing">
          <p>
            Foodz Lizts uses artificial intelligence (AI) to enhance your experience, including:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Meal Planning:</strong> AI generates personalized meal suggestions based on your pantry items and dietary preferences.</li>
            <li><strong>Receipt Scanning:</strong> AI extracts item data from receipt photos for budget tracking.</li>
            <li><strong>Brand Recognition:</strong> AI identifies grocery brands and categorizes items automatically.</li>
            <li><strong>Smart Suggestions:</strong> AI learns from your purchase history to suggest frequently bought items.</li>
          </ul>
          <p>
            AI processing is performed using third-party language models. Your name and email are <strong>not</strong> sent to AI services. Only item names, dietary preferences, and anonymized shopping data are shared for processing.
          </p>
        </Section>

        <Section title="5. Third-Party Data Sources">
          <p>
            Our Service displays weekly grocery deals, product prices, and store information sourced from
            third-party providers. <strong>This data is provided for informational purposes only and may not
            always be accurate, complete, or up-to-date.</strong> We do not guarantee the accuracy of pricing,
            availability, or promotional offers displayed in the app. Always verify deals and prices directly
            with the retailer before making purchasing decisions.
          </p>
          <p>
            Recipe and nutritional data is sourced from third-party APIs and should be used as general guidance only.
          </p>
        </Section>

        <Section title="6. Data Sharing">
          <p>We do <strong>not</strong> sell your personal information. We share data only with:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>PayPal:</strong> For subscription payment processing and referral payouts</li>
            <li><strong>Resend:</strong> For transactional emails (account alerts, password resets, deal notifications)</li>
            <li><strong>OpenStreetMap / Photon:</strong> For store location lookups (only coordinates and store search queries are sent, not personal data)</li>
            <li><strong>AI Services (OpenAI):</strong> For meal planning, brand recognition, and receipt scanning (anonymized data only, no personally identifiable information)</li>
            <li><strong>Third-party recipe APIs:</strong> We send your dietary preferences (not your name or email) to generate personalized suggestions</li>
          </ul>
        </Section>

        <Section title="7. Push Notifications">
          <p>
            With your permission, we send push notifications including:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Deal alerts when items on your grocery list go on sale</li>
            <li>Subscription reminders during the last 3 days of your 14-day free trial</li>
            <li>Periodic subscription reminders after your trial ends (approximately once per week as a push notification, and once per 30 days as an email) — you can disable these at any time</li>
            <li>Activity notifications for your community deal posts (likes, comments, saves)</li>
          </ul>
          <p>You can disable push notifications at any time in your device settings or within the app.</p>
        </Section>

        <Section title="8. Data Retention & Deletion">
          <p>
            We retain your data for as long as your account is active. You can delete your account at any time
            from Settings, which will permanently remove your personal data, grocery lists, meal plans,
            community posts, and all associated information. After deletion, your data cannot be recovered.
          </p>
          <p>
            Receipt images are processed in real-time and are not permanently stored. Extracted receipt data (item names, prices) is retained with your account data.
          </p>
        </Section>

        <Section title="9. Your Rights (California / CCPA)">
          <p>If you are a California resident, you have the right to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Know what personal information we collect about you</li>
            <li>Request deletion of your personal information</li>
            <li>Opt out of the sale of personal information (we do not sell your data)</li>
            <li>Non-discrimination for exercising your privacy rights</li>
          </ul>
          <p>To exercise these rights, contact us at <strong>support@foodzlizts.com</strong> or use the account deletion feature in Settings.</p>
        </Section>

        <Section title="10. Children's Privacy">
          <p>
            Our Service is not intended for children under 13 years of age. We do not knowingly collect
            personal information from children under 13. If you believe a child has provided us with
            personal information, please contact us at support@foodzlizts.com.
          </p>
        </Section>

        <Section title="11. Security">
          <p>
            We use industry-standard security measures to protect your data, including encrypted passwords
            and PINs (bcrypt hashing), secure HTTPS connections, JWT token-based authentication, and encrypted
            database storage. Payment processing is handled entirely by PayPal's secure infrastructure.
            However, no method of electronic transmission or storage is 100% secure.
          </p>
        </Section>

        <Section title="12. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. We will notify you of material changes
            by posting the updated policy in the app and, where required, via email or push notification.
            Your continued use of the Service after changes constitutes acceptance of the updated policy.
          </p>
        </Section>

        <Section title="13. Contact Us">
          <p>
            If you have questions about this Privacy Policy or your data, contact us at:<br />
            <strong>Email:</strong> support@foodzlizts.com
          </p>
        </Section>
      </div>
    </div>
  );
}
